import { ArrowRight } from "lucide-react"
import { createFileRoute } from "@tanstack/react-router"
import { UseCasePage } from "@/components/use-case-page"
import { publicPageHead } from "@/lib/seo"

const title = "Gaming Shorts Generator for Gameplay & Facecam | intoshort"
const description =
  "Turn gameplay into shorts with facecam and captions. Create vertical gaming clips, review the detected layout and adjust framing before export."

export const Route = createFileRoute("/tools/gameplay-facecam-to-shorts")({
  head: () =>
    publicPageHead({ path: "/tools/gameplay-facecam-to-shorts", title, description }),
  component: GameplayFacecamToShortsPage,
})

function GameplayFacecamToShortsPage() {
  return (
    <UseCasePage
      badge="Gameplay and Facecam"
      eyebrow="Gaming shorts maker"
      title="Gaming shorts generator for gameplay and facecam."
      lead="Make gaming shorts from your recordings or public VODs. For 9:16 renders, intoshort can place a detected facecam above the gameplay with captions between them. Review the composition and adjust it in the editor. Unstable detection falls back to a center crop."
      sourceLine="Automatic layout is applied to selected passages, not the entire source video."
      proofTitle="A layout decision based on the footage, with a safe fallback."
      proofCopy="The product does not assume every corner box is a facecam. It checks the selected passage for a stable face region before using the split gameplay layout."
      proofPoints={[
        "A stable facecam can be stacked above gameplay in a 40/60 vertical composition.",
        "Captions sit between the two visual areas to avoid covering the main action.",
        "Missing or unstable detection falls back to a center crop instead of forcing a broken layout.",
      ]}
      visual={
        <section
          className="use-case-visual page-width"
          aria-labelledby="layout-example-title"
        >
          <header>
            <p>Layout example</p>
            <h2 id="layout-example-title">
              One source, a vertical composition.
            </h2>
          </header>
          <div>
            <figure>
              <figcaption>
                <strong>Original footage</strong>
                <span>16:9</span>
              </figcaption>
              <img
                src="/images/framing-source-ai.png"
                alt="AI-generated tactical gameplay scene with a fictional streamer in a facecam overlay."
                width={1672}
                height={941}
                loading="lazy"
              />
            </figure>
            <ArrowRight aria-hidden="true" />
            <figure>
              <figcaption>
                <strong>Vertical draft</strong>
                <span>9:16</span>
              </figcaption>
              <img
                src="/images/framing-vertical-ai.png"
                alt="Illustrative vertical layout with a fictional streamer above gameplay and an example caption between them."
                width={941}
                height={1672}
                loading="lazy"
              />
            </figure>
          </div>
          <p>
            AI-generated illustration, not an actual export. The final layout
            depends on the detected facecam and the selected source passage.
          </p>
        </section>
      }
      stepsTitle="The clip is selected before the frame is rebuilt."
      stepsCopy="Framing follows the editorial selection. That keeps the visual analysis focused on the part you may actually export instead of processing the full VOD as if every scene used the same layout."
      steps={[
        {
          title: "Select the passage",
          copy: "AI proposes a source-backed moment and intoshort validates the timing before the render starts.",
        },
        {
          title: "Choose a 9:16 output",
          copy: "Gameplay and facecam detection is used for new vertical renders. A 16:9 export keeps the conventional full-frame treatment.",
        },
        {
          title: "Analyze the selected footage",
          copy: "The render checks for a stable face region across the chosen passage before committing to the split layout.",
        },
        {
          title: "Review the composition",
          copy: "Play the draft, check the action and captions, then refine or reject it before export.",
        },
      ]}
      limitsTitle="Automatic framing cannot infer every overlay correctly."
      limits={[
        "The split layout requires stable facecam detection in the selected passage. Scene changes, masks, avatars or multiple faces can trigger the fallback.",
        "A center crop is used when detection is absent, unstable or fails. That protects the render, but it may not preserve every important HUD element.",
        "The illustration shows the intended structure, not a promise that every source will produce the same crop or proportions.",
      ]}
      questions={[
        {
          question: "What happens if my facecam disappears?",
          answer:
            "If the face region is not stable enough across the selected passage, intoshort uses the fallback crop instead of forcing the gameplay-and-facecam layout.",
        },
        {
          question: "Does this change my original video?",
          answer:
            "No. The source remains unchanged. The layout is created for the new short render and can be reviewed before export.",
        },
        {
          question: "Will captions cover the gameplay?",
          answer:
            "In the split layout, captions are positioned between the facecam and gameplay areas. You can still review and adjust their style, size and position.",
        },
      ]}
      related={[
        {
          eyebrow: "Stream source",
          title: "Twitch VOD to Shorts",
          copy: "Find passages in a public stream before applying the vertical layout.",
          href: "/tools/twitch-vod-to-shorts",
        },
        {
          eyebrow: "Any supported source",
          title: "Long Video to Shorts",
          copy: "Start from an upload, YouTube video or Twitch VOD.",
          href: "/tools/long-video-to-short-video",
        },
      ]}
    />
  )
}
